import React from 'react';
import { useTranslation } from 'react-i18next';
import { ComboSelect } from '../../atoms';
import { styled } from '@mui/material/styles';
import { Grid, useTheme } from '@mui/material';

export interface SourceItem {
    accountId: string;
    accountName: string;
    amount?: number;
    frequency?: string;
    recurringContributions?: number;
    [key: string]: any; // Allow for additional properties
}

export interface FundingDrawersDropdownProps {
    // Data props
    sources: SourceItem[];
    selectedSourceId: string;

    // Labels and text
    title: string;
    placeholderText?: string;

    // eslint-disable-next-line
    onSourceSelect: (sourceId: string) => void;
    // eslint-disable-next-line
    onSourceEdit: (sourceId: string) => void;
    onResetForm: () => void;
    // eslint-disable-next-line
    onModeChange: (mode: 'add' | 'edit') => void;
    dropdownTestId?: string;
}

const GridContainer = styled(Grid)(() => ({
    display: 'flex',
    flexDirection: 'column',
    '.MuiFormControl-root': {
        width: '100%'
    },
    gap: '0.5rem'
}));

export const FundingDrawersDropdown: React.FC<FundingDrawersDropdownProps> = ({
    sources,
    selectedSourceId,
    title,
    placeholderText,
    onSourceSelect,
    onSourceEdit,
    onResetForm,
    onModeChange,
    dropdownTestId = 'saved-sources-dropdown'
}) => {
    const { t } = useTranslation();
    const theme = useTheme();

    const placeholder = placeholderText || t('TEXT_DROP_DOWN_PLACEHOLDER');

    const handleSourceChange = (sourceId: string) => {
        onSourceSelect(sourceId);
        if (sourceId) {
            onSourceEdit(sourceId);
        } else {
            // Reset form when no source is selected
            onModeChange('add');
            onResetForm();
        }
    };

    return sources?.length > 0 ? (
        <GridContainer item xs={12} key="sourceTypeContainer">
            <ComboSelect
                key={`sources-dropdown-${sources.length}`}
                id={`funding-sources-select-${dropdownTestId}`}
                name={`funding-sources-select-${dropdownTestId}`}
                label={title}
                size="small"
                onSelectChange={handleSourceChange}
                selectedValue={selectedSourceId || ''}
                options={[
                    ...sources
                        .slice()
                        .reverse()
                        .map((source) => ({
                            label: source.accountName,
                            value: source.accountId || ''
                        }))
                ]}
                placeholder={`${placeholder} (${sources.length})`}
                data-testid={dropdownTestId}
                renderValue={(value) => {
                    if (!value || value === '') {
                        return (
                            <em style={{ color: theme.palette.semantic.int.link, fontStyle: 'normal' }}>
                                {`${placeholder} (${sources.length})`}
                            </em>
                        );
                    }
                    const selectedSource = sources.find((source) => source.accountId === value);
                    return selectedSource ? selectedSource.accountName : value;
                }}
                MenuProps={{
                    PaperProps: {
                        sx: {
                            border: `1px solid ${theme.palette.semantic.bd.default}`,
                            borderRadius: '8px',
                            '& .MuiMenuItem-root': {
                                color: theme.palette.semantic.text.default
                            }
                        }
                    }
                }}
            />
        </GridContainer>
    ) : null;
};
